import { useEffect, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import { api } from "@/lib/api";
import { notify as desktopNotify } from "@/lib/desktop-notify";
import { pushPresenceDelta } from "@/hooks/usePresence";

// ---------------------------------------------------------------------------
// Shapes
// ---------------------------------------------------------------------------

export interface NotificationRow {
  id: string;
  title: string;
  message: string;
  link: string;
  is_read: boolean;
  created_at: string;
  actor_id: string | null;
  actor_name: string | null;
}

interface Paginated<T> {
  count: number;
  next: string | null;
  previous: string | null;
  results: T[];
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** GET /api/v1/notifications/  — paginated, newest first. */
export function useNotifications(opts: { unreadOnly?: boolean; page?: number } = {}) {
  return useQuery<Paginated<NotificationRow>>({
    queryKey: ["notifications", "list", opts],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (opts.unreadOnly) params.set("unread_only", "1");
      if (opts.page)       params.set("page", String(opts.page));
      const { data } = await api.get<Paginated<NotificationRow>>(
        `/notifications/?${params.toString()}`,
      );
      return data;
    },
  });
}

/** GET /api/v1/notifications/unread-count/ — polled by the bell. */
export function useUnreadCount() {
  return useQuery<{ unread: number }>({
    queryKey: ["notifications", "unread-count"],
    queryFn: async () => {
      const { data } = await api.get<{ unread: number }>(
        "/notifications/unread-count/",
      );
      return data;
    },
    // Light fallback poll. The WS push is the real-time path; this just
    // keeps the badge fresh if the socket drops.
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

export function useMarkRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post<NotificationRow>(
        `/notifications/${id}/mark-read/`,
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

export function useMarkAllRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { data } = await api.post<{ updated: number }>(
        "/notifications/mark-all-read/",
      );
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["notifications"] }),
  });
}

// ---------------------------------------------------------------------------
// WebSocket — live push from apps.private.consumers.NotificationConsumer
// at /ws/notifications/. The connection sticks for the lifetime of the
// app shell; messages just invalidate the React Query cache so the
// dropdown + bell badge re-fetch from REST. Keeps the source of truth
// single (REST), uses the socket only as a wake-up signal.
// ---------------------------------------------------------------------------

export function useNotificationSocket(enabled: boolean) {
  const qc = useQueryClient();
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!enabled) return;
    if (typeof window === "undefined") return;

    // Vite proxies /ws/ → ws://localhost:8000/ws/ in dev.
    const proto = window.location.protocol === "https:" ? "wss" : "ws";
    const url = `${proto}://${window.location.host}/ws/notifications/`;

    let alive = true;
    let ws: WebSocket | null = null;
    let retry = 0;

    function connect() {
      if (!alive) return;
      ws = new WebSocket(url);
      wsRef.current = ws;

      ws.onopen = () => {
        retry = 0;
      };

      ws.onmessage = (evt) => {
        // The same socket multiplexes notification pushes AND presence
        // change deltas. Parse first, branch on `type`.
        let data: {
          type?: string;
          notification?: { title?: string; message?: string; link?: string; id?: string };
          user_id?: string;
          online?: boolean;
        } | null = null;
        try { data = JSON.parse(evt.data as string); } catch { /* malformed */ }

        if (data?.type === "presence.change" && data.user_id) {
          // Module-level set in usePresence so every avatar can read it
          // without each hook instance opening its own WS.
          pushPresenceDelta(data.user_id, !!data.online);
          return;
        }

        if (data?.type === "dm.message") {
          // New direct message landed for me (or my own send echoed
          // back from another tab). Invalidate both caches; the message
          // payload is in the event but we lean on REST refetch as the
          // source of truth (same pattern the forum uses).
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const convId = (data as any).conversation_id as string | undefined;
          if (convId) {
            qc.invalidateQueries({ queryKey: ["conversations", "messages", convId] });
          }
          qc.invalidateQueries({ queryKey: ["conversations"] });
          return;
        }

        if (data?.type === "dm.read") {
          // The OTHER user opened the conversation — flip the read
          // tick on every message I sent up to their read_at. Easiest
          // way is to invalidate the messages cache so the next refetch
          // brings down the updated read_by_other flags.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const convId = (data as any).conversation_id as string | undefined;
          if (convId) {
            qc.invalidateQueries({ queryKey: ["conversations", "messages", convId] });
          }
          return;
        }

        // Default path: wake the notification caches + maybe pop a
        // desktop alert. REST stays source of truth.
        qc.invalidateQueries({ queryKey: ["notifications"] });
        if (data?.type === "notification.new" && data.notification?.title) {
          desktopNotify({
            title: data.notification.title,
            body:  data.notification.message,
            link:  data.notification.link || undefined,
            // Same id never re-pops; new ones replace the previous.
            tag:   data.notification.id != null ? `kms-n-${data.notification.id}` : undefined,
          });
        }
      };

      ws.onclose = () => {
        wsRef.current = null;
        if (!alive) return;
        // Exponential-ish backoff, capped at ~30s.
        retry += 1;
        const delay = Math.min(30_000, 1_000 * 2 ** Math.min(retry, 5));
        setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try { ws?.close(); } catch { /* noop */ }
      };
    }

    connect();
    return () => {
      alive = false;
      try { wsRef.current?.close(); } catch { /* noop */ }
      wsRef.current = null;
    };
  }, [enabled, qc]);
}
